/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-plusplus */
/* eslint-disable react/no-array-index-key */
import t from 'react-translate';
import _ from 'lodash';
import { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import NvModal, { ModalType } from 'shared/components/nv-modal';
import MentoringProgramContext from 'athena/components/mentoring-program/context';
import {
  CompletionStatus,
  MentoringProgramEnrollment,
  StepKey,
} from 'redux/schemas/models/mentoring-program-enrollments';
import { getMentoringProgramEnrollment as getMentoringProgramEnrollmentSelector } from 'redux/selectors/mentoring-program-enrollments';
import { css } from '@emotion/react';
import { halfSpacing } from 'styles/global_defaults/scaffolding';
import { gray3, gray6, white } from 'styles/global_defaults/colors';
import { Button } from 'react-bootstrap';
import { FormProvider, useForm } from 'react-hook-form';
import { useAppDispatch } from 'redux/store';
import { wrapThunkAction } from 'redux/utils';
import { createMentorshipProgramProfileAnswers } from 'redux/actions/mentoring-program-profile-answers';
import { setMentoringProfileReviewed } from 'redux/actions/mentoring-program-enrollments';
import { useInView } from 'react-intersection-observer';
import { isEmpty } from 'underscore';
import { openConfirmationDialog } from 'redux/actions/confirmation-dialogs';
import { setProfilePicture, updateBasicUserProfile } from 'redux/actions/users';
import { AccountField } from 'athena/components/mentoring-program/constants';
import { AngularContext } from 'react-app';
import { config } from '@config/pendo.config.json';
import {
  areAllRequiredFieldsAnswered,
  isAnyRequiredFieldUnanswered,
} from '../utils';
import ProgramQuestions from './questions/program-questions';
import OrgQuestions from './questions/org-questions';
import { getMentoringProgram } from 'redux/actions/mentoring-programs';

const modalStyles = css`
  &.bs4-modal {
    .bs4-modal-dialog {
      .bs4-modal-body {
        padding: 0;
        overflow: hidden;
      }
    }
  }
`;
const styles = css`
  .profile-content {
    // (height of modal title: 60px) + (height of footer: 105px) = 165px
    height: min(654px, calc(100vh - 165px));
    overflow-y: auto;
    padding: ${halfSpacing}px 167px;
  }

  .profile-footer {
    position: sticky;
    bottom: 0;
    background-color: ${white};
    border-bottom-left-radius: ${halfSpacing}px;
    border-bottom-right-radius: ${halfSpacing}px;

    height: 105px;
    border-top: 1px solid ${gray6};
    button[disabled] {
      background-color: ${gray6};
      border-color: ${gray6};
      color: ${gray3};
    }
  }
`;

type ProfileModalProps = {
  title?: string;
  setShowModal: (showModal: boolean) => void;
  onClose?: (remaining: number, showModal: boolean) => void;
};

const ProfileModal = ({ title, setShowModal, onClose }: ProfileModalProps) => {
  const dispatch = useAppDispatch();
  const { injectServices } = useContext(AngularContext);
  const [$state, CurrentUserManager] = injectServices([
    '$state',
    'CurrentUserManager',
  ]);
  const { mentoringProgram } = useContext(MentoringProgramContext);
  const { id: mentorshipProgramId, mentorshipProgramEnrollment: enrollmentId } =
    mentoringProgram || {};
  const mentorshipProgramEnrollment: MentoringProgramEnrollment = useSelector(
    (state) => getMentoringProgramEnrollmentSelector(state, enrollmentId)
  );
  const { user, states: enrollmentStates } = mentorshipProgramEnrollment || {};
  const questions = [OrgQuestions, ProgramQuestions];
  const { ref: profileEndRef, inView: hasProfileBeenViewed } = useInView();
  const [remainingQuestions, setRemainingQuestions] = useState(0);
  const [showRequiredQuestionsModal, setShowRequiredQuestionsModal] =
    useState(false);
  const methods = useForm();
  const {
    handleSubmit,
    reset,
    formState: { isDirty },
    getValues,
  } = methods;

  const setProfileReviewCompleted = () =>
    dispatch(
      setMentoringProfileReviewed({
        programId: mentorshipProgramId,
        enrollmentId,
      })
    );

  const getRemainingRequiredQuestions = () => {
    const data = getValues();
    const dataValues = Object.values(data);
    const remaining = dataValues.filter(
      ({ isRequired, answer }) =>
        isRequired && (answer === '' || answer === null || answer.length === 0)
    ).length;
    return remaining;
  };

  useEffect(() => {
    dispatch(getMentoringProgram(mentorshipProgramId));
  }, []);

  useEffect(() => {
    if (hasProfileBeenViewed) {
      if (areAllRequiredFieldsAnswered(enrollmentStates)) {
        setProfileReviewCompleted();
      }
    }
  }, [hasProfileBeenViewed]);

  const getModalTitle = () => {
    let modalTitle =
      t.MENTORING_PROGRAMS.PARTICIPANT_HOME.GET_STARTED.PROFILE_MODAL.TITLE.DEFAULT();
    if (
      enrollmentStates[StepKey.PROFILE_REVIEW]?.status ===
      CompletionStatus.COMPLETED
    ) {
      modalTitle =
        t.MENTORING_PROGRAMS.PARTICIPANT_HOME.GET_STARTED.PROFILE_MODAL.TITLE.EDIT_PROFILE();
    } else if (isAnyRequiredFieldUnanswered(enrollmentStates)) {
      modalTitle =
        t.MENTORING_PROGRAMS.PARTICIPANT_HOME.GET_STARTED.PROFILE_MODAL.TITLE.COMPLETE_PROFILE();
    } else {
      modalTitle =
        t.MENTORING_PROGRAMS.PARTICIPANT_HOME.GET_STARTED.PROFILE_MODAL.TITLE.REVIEW_PROFILE();
    }
    return modalTitle;
  };

  type QuestionAnswerType = {
    name: string;
    isRequired: boolean;
    mentorshipProgramProfileQuestionId: number;
    answer: string | string[] | null;
  };

  const onSubmit = async (
    data: {
      [key: number]: QuestionAnswerType;
      firstName?: string;
      lastName?: string;
    },
    event,
    displayRequiredQuestionsModal = true
  ) => {
    if (!isEmpty(data)) {
      const { firstName, lastName } = data;
      if (firstName || lastName) {
        dispatch(
          updateBasicUserProfile({
            user: {
              firstName: firstName || user.firstName,
              lastName: lastName || user.lastName,
            },
          })
        );
        delete data.firstName;
        delete data.lastName;
      }
      const dataValues = Object.values(data) as QuestionAnswerType[];
      const remaining = dataValues.filter(
        ({ isRequired, answer }) =>
          isRequired &&
          (answer === '' || answer === null || answer.length === 0)
      ).length;
      const profileFieldIndex = dataValues.findIndex(
        (question) => question.name === AccountField.PICTURE_FILE_NAME
      );
      if (profileFieldIndex !== -1) {
        const profileField = dataValues[profileFieldIndex];
        if (profileField.answer instanceof File) {
          await dispatch(
            setProfilePicture({ image: profileField.answer, userId: user.id })
          ).then((action) =>
            CurrentUserManager.user.updateFromReact({
              profilePicture: action.payload,
              profilePictureUrl: action.payload,
            })
          );
          dataValues.splice(profileFieldIndex, 1);
        }
      }
      const saveProfileAnswersPayload = {
        mentorshipProgramId,
        mentorshipProgramProfileAnswers: dataValues.map((questionAnswered) => {
          const { mentorshipProgramProfileQuestionId, answer } = questionAnswered;
          if (typeof mentorshipProgramProfileQuestionId === 'number') {
            return {
              ...questionAnswered,
              mentorshipProgramEnrollmentId: enrollmentId
            }
          } else {
            return {
              answer,
              mentorshipProgramEnrollmentId: enrollmentId,
              mentorshipProgramEnrollmentSettingQuestion: mentorshipProgramProfileQuestionId,
            }
          }
        }),
        enrollmentId,
      };
      wrapThunkAction(
        dispatch(
          createMentorshipProgramProfileAnswers(saveProfileAnswersPayload),
        ),
      ).then(() => {
        if (
          remaining === 0 && enrollmentStates[StepKey.PROFILE_REVIEW].status !== CompletionStatus.COMPLETED
        ) {
          setProfileReviewCompleted();
        }
        resetShowProfileParam();
        setShowModal(false);
      }).then(() => {
        setRemainingQuestions(remaining);
        setShowRequiredQuestionsModal(true);
        onClose?.(remaining, displayRequiredQuestionsModal);
      });
    }
  };

  const resetShowProfileParam = () => {
    const params = { showProfile: false };
    $state.go($state.current, params, { notify: false });
  };

  const onCloseModal = () => {
    if (isDirty) {
      dispatch(
        openConfirmationDialog({
          title: t.FORM.UNSAVED_CHANGES.ARE_YOU_SURE(),
          bodyText:
            t.MENTORING_PROGRAMS.PARTICIPANT_HOME.GET_STARTED.PROFILE_MODAL.UNSAVED_CHANGES(),
          confirmText: t.FORM.YES_SURE(),
          onConfirm: () => {
            setShowModal(false);
            reset();
          },
        })
      );
    } else {
      const remaining = getRemainingRequiredQuestions();
      // When closing the modal, if all required questions are answered, set the profile review as completed
      if (
        remaining === 0 &&
        enrollmentStates[StepKey.PROFILE_REVIEW].status !==
          CompletionStatus.COMPLETED
      ) {
        onSubmit(getValues(), null, false);
      }
      setShowModal(false);
    }
    resetShowProfileParam();
  };

  const buttonLabel =
    t.MENTORING_PROGRAMS.PARTICIPANT_HOME.GET_STARTED.PROFILE_MODAL.SAVE();

  return (
    <>
      <NvModal
        width={960}
        height='unset'
        type={ModalType.FIXED}
        header={title || getModalTitle()}
        body={
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div css={styles}>
                <div className='profile-content position-relative'>
                  <div className='heading-4 pt-4'>
                    {t.MENTORING_PROGRAMS.PARTICIPANT_HOME.GET_STARTED.PROFILE_MODAL.SUBTITLE()}
                  </div>
                  <div className='text-body pt-2 pb-5'>
                    {t.MENTORING_PROGRAMS.PARTICIPANT_HOME.GET_STARTED.PROFILE_MODAL.DESCRIPTION()}
                  </div>
                  {questions.map((Questions, index) => (
                    <Questions
                      key={index}
                      enrollmentId={enrollmentId}
                      mentorshipProgramId={mentorshipProgramId}
                    />
                  ))}
                </div>
                <div ref={profileEndRef} />
                <div className='d-flex align-items-center justify-content-center profile-footer'>
                  <Button
                    size='lg'
                    type='submit'
                    disabled={!isDirty}
                    data-qa={
                      config.pendo.athena.mentorshipProgram.participantHome
                        .steps.profile.save
                    }
                    data-qa-id={`${config.pendo.athena.mentorshipProgram.participantHome.steps.profile.save}_${mentorshipProgramId}_${enrollmentId}`}
                  >
                    {buttonLabel}
                  </Button>
                </div>
              </div>
            </form>
          </FormProvider>
        }
        show
        modalStyles={modalStyles}
        onClose={onCloseModal}
      />
    </>
  );
};

export default ProfileModal;
